/**
 * Main script.
 *
 * @author Simon Lagerlöf <contact@smn.codes>
 * @copyright 2024 Simon Lagerlöf
 * @since 1.0.0
 */

import Alpine from 'alpinejs';
import Privcy from 'privcy';

import { siteHeader } from './site/header';
import { newswireBanner } from './site/newswire-banner';
import type AlpineComponent from './types/alpine-component';
import { adjustPictureAttributions } from './site/picture';
import hero from './partials/hero';
declare global {
  interface Window {
    Alpine: typeof Alpine;
    $controllers: Record<string, AlpineComponent<any>>;
  }
}

interface Controller {
  default?: () => void;
  alpineData?: AlpineComponent<any>;
}

const bannerOptions = document
  .querySelector('script[data-privcy-options]')
  ?.getAttribute('data-privcy-options');

new Privcy({
  cookiePrefix: 'rd_privacy',
  title: 'Privacy',
  description:
    'By clicking "Accept", you consent to us collecting data for the purposes described in our privacy policy. We use necessary cookies to keep track of your choice.',
  categories: {
    analytics: {
      name: 'Analytics',
      description:
        'We use analytics tools to collect statistics about our visitors.',
    },
    social: {
      name: 'Social',
      description:
        'We use extenal platforms like YouTube to embed videos on the website.',
    },
  },
  ...JSON.parse(bannerOptions ?? ''),
});

(async () => {
  window.Alpine = Alpine;

  /**
   * Site wide controllers.
   */
  document.addEventListener('alpine:init', () => {
    window.$controllers = {
      siteHeader: siteHeader,
      newswireBanner: newswireBanner,
    };
  });

  /**
   * Template specific controllers
   */

  await (async () => {
    const template: string =
      document.body.getAttribute('data-template') ?? 'default';

    const templateControllers: Record<string, () => Promise<any>> = {
      faq: () => import('./templates/faq'),
      researchers: () => import('./templates/researchers'),
      calendar: () => import('./templates/calendar'),
    };

    const controller = templateControllers[template];

    if (controller) {
      await controller().then(
        ({ default: main, alpineData }: Controller) => {
          if (typeof main === 'function') {
            main();
          }

          if (typeof alpineData === 'function') {
            document.addEventListener('alpine:init', () => {
              window.$controllers[template] = alpineData;
            });
          }
        },
      );
    }
  })();

  Alpine.start();

  adjustPictureAttributions();

  hero();
})();
